import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { fontWeightEnum, Text, textVariantsEnum } from '@/components/Text';
import { FiClock } from 'react-icons/fi';
import styles from './UserAttendance.module.scss';
import {
  userLeaveBalanceConfig,
  userLeaveEnum,
} from './UserAttendance.constants';
import { addNotification, notificationStates } from '@/components/Notification';
import { UserAttendanceView } from './UserAttendance.view';
import {
  DEMO_MEMBERS_OPTIONS,
  buildDemoTimeLogInfo,
  DEMO_PREVIOUS_MONTH_COUNTS,
} from './UserAttendance.demoData';

const emptyLeaveCounts = () => ({
  present: 0,
  absent: 0,
  weekOff: 0,
  holiday: 0,
  sickLeave: 0,
  casualLeave: 0,
  plannedLeave: 0,
  unpaidLeave: 0,
});

export const UserAttendanceDemoComponent = () => {
  const [selectOption, setSelectOption] = useState(DEMO_MEMBERS_OPTIONS[0]);
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const timeLogInfo = useMemo(
    () => buildDemoTimeLogInfo(currentMonth),
    [currentMonth]
  );

  const timeLogByDate = useMemo(() => {
    const map = new Map();
    timeLogInfo.forEach((item) => map.set(item.date, item));
    return map;
  }, [timeLogInfo]);

  const getDayContent = useCallback(
    (dateStr) => {
      const log = timeLogByDate.get(dateStr);
      if (!log) return null;
      return (
        <div className={styles.dateCellWrapper}>
          <div className={styles.statusPill} style={{ background: log.color }}>
            <span className={styles.statusDot} />
            <Text
              variant={textVariantsEnum.Text_xs}
              fontWeight={fontWeightEnum.Normal}
            >
              {log.Status}
            </Text>
          </div>
          {log.key === userLeaveEnum.present && (
            <div className={styles.durationRow}>
              <FiClock size={11} />
              <Text variant={textVariantsEnum.Text_xxs} color="gray-500">
                8h 30m
              </Text>
            </div>
          )}
        </div>
      );
    },
    [timeLogByDate]
  );

  const leaveBalanceConfig = useMemo(() => {
    const counts = emptyLeaveCounts();
    timeLogInfo.forEach((item) => {
      counts[item.key]++;
    });
    return counts;
  }, [timeLogInfo]);

  const prevMonthShortLabel = useMemo(
    () => moment(currentMonth).subtract(1, 'month').format('MMM'),
    [currentMonth]
  );

  const trendByKey = useMemo(() => {
    const result = {};
    userLeaveBalanceConfig.forEach(({ key }) => {
      const prev = DEMO_PREVIOUS_MONTH_COUNTS[key] || 0;
      if (prev === 0) {
        result[key] = null;
        return;
      }
      const current = leaveBalanceConfig[key] || 0;
      const pct = Math.round(((current - prev) / prev) * 100);
      result[key] = { pct: Math.abs(pct), direction: pct >= 0 ? 'up' : 'down' };
    });
    return result;
  }, [leaveBalanceConfig]);

  const totalDaysTracked = timeLogInfo.length;
  const daysInSelectedMonth = useMemo(
    () => moment(currentMonth).daysInMonth(),
    [currentMonth]
  );
  const workingDays = Math.max(
    daysInSelectedMonth - (leaveBalanceConfig.weekOff || 0),
    0
  );
  const attendancePct =
    workingDays > 0
      ? Number(((leaveBalanceConfig.present / workingDays) * 100).toFixed(1))
      : 0;

  const donutSegments = useMemo(
    () =>
      userLeaveBalanceConfig.map((item) => {
        const value = leaveBalanceConfig[item.key] || 0;
        return {
          key: item.key,
          label: item.label,
          value,
          percentage:
            totalDaysTracked > 0
              ? Math.round((value / totalDaysTracked) * 100)
              : 0,
          color: item.color,
        };
      }),
    [leaveBalanceConfig, totalDaysTracked]
  );

  const onExportMember = () => {
    addNotification({
      message: 'Exporting attendance is not available in demo mode.',
      level: notificationStates.info,
    });
  };

  return (
    <UserAttendanceView
      showInitialLoader={false}
      membersError={false}
      membersErrorObj={null}
      refetchMembers={() => {}}
      activeTeamId="demo-team"
      options={DEMO_MEMBERS_OPTIONS}
      selectOption={selectOption}
      onSelectChange={setSelectOption}
      membersLoading={false}
      currentMonth={currentMonth}
      onMonthChange={setCurrentMonth}
      onPrevMonth={() =>
        setCurrentMonth(moment(currentMonth).subtract(1, 'month').toDate())
      }
      onNextMonth={() =>
        setCurrentMonth(moment(currentMonth).add(1, 'month').toDate())
      }
      exportLoading={false}
      isExportingMember={false}
      isExportingTeam={false}
      onExportMember={onExportMember}
      trendByKey={trendByKey}
      prevMonthShortLabel={prevMonthShortLabel}
      leaveBalanceConfig={leaveBalanceConfig}
      isCalendarLoading={false}
      getDayContent={getDayContent}
      totalDaysTracked={totalDaysTracked}
      donutSegments={donutSegments}
      workingDays={workingDays}
      attendancePct={attendancePct}
    />
  );
};
